import React, { useCallback } from "react";
import { Box, Typography } from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import { useStyles } from "./AreasList.styles";
import { useTranslation } from "react-i18next";
import { AddAreaOrFunctionalLocationModal } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/AddAreaOrFunctionalLocationModal/AddAreaOrFunctionalLocationModal.view";
import { AreaCreationForm } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/forms/AreaCreationForm/AreaCreationForm.view";
import { IArea } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import List from "@mui/material/List";
import { FunctionalLocationCreationForm } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/forms/FunctionalLocationCreationForm/FunctionalLocationCreationForm.view";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import useRolesPermissions from "hooks/useRolesPermissions";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableAreaCard } from "./components/SortableAreaCard";
import { AreaItem } from "./components/AreaItem";
import { useAreasReorder } from "./hooks/useAreasReorder";
import { useAreasListContext } from "./context/AreasListContextProvider";

export const AreasList = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        areas,
        areaTabs,
        openAreaModal,
        refetch,
        setAreas,
        handleOpenAreaModal,
        handleCloseAreaModal,
        selectedAreaToEdit,
        openFunctionalLocationModal,
        handleCloseFunctionalLocationModal,
        handleCloseDeletionModal,
        selectedAreaToDelete,
        selectedFunctionalLocationToEdit,
        selectedFunctionalLocationToDelete,
    } = useAreasListContext();

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    const { updateAreaData } = FailureDetailsEntity.UpdateArea();
    const { updateFunctionalLocationData } = FailureDetailsEntity.UpdateFunctionalLocation();
    const { setAreasOrder } = FailureDetailsEntity.SetAreasOrderByMachineCenter();

    const {
        isDraggingAreas,
        onDragMove: onAreasDragMove,
        onDragEnd: onAreasDragEnd,
    } = useAreasReorder({ areas, setAreas, refetch, setAreasOrder });

    const handleDeleteArea = useCallback(async () => {
        if (selectedAreaToDelete) {
            const areaValues = {
                disabled_at: new Date(),
            };

            try {
                const response = await updateAreaData({
                    variables: { area_id: selectedAreaToDelete.area_id, changes: areaValues },
                });
                if (response) {
                    refetch();
                }
            } catch {
                return;
            }
        }
    }, [refetch, selectedAreaToDelete, updateAreaData]);

    const handleDeleteFunctionalLocation = useCallback(async () => {
        if (selectedFunctionalLocationToDelete) {
            const functionalLocationValues = {
                disabled_at: new Date(),
            };

            try {
                const response = await updateFunctionalLocationData({
                    variables: {
                        functional_location_id: selectedFunctionalLocationToDelete.functional_location_id,
                        changes: functionalLocationValues,
                    },
                });

                if (response) {
                    refetch();
                }
            } catch {
                return;
            }
        }
    }, [refetch, selectedFunctionalLocationToDelete, updateFunctionalLocationData]);

    return (
        <Box className={classes.container}>
            <Box className={classes.titleContainer}>
                {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters && (
                    <button className={`${classes.button} ${classes.titleContainer}`} onClick={handleOpenAreaModal}>
                        <AddCircleOutlinedIcon />
                        <Typography className={classes.title} variant="subtitle2">
                            {t("failure_details_setup.add_new_area").toUpperCase()}
                        </Typography>
                    </button>
                )}
            </Box>
            {areas && areas.length > 0 && (
                <List>
                    {CORPORATE_PERMISSIONS?.corporationPermissions?.canDragAndDropMachineCenters ? (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragMove={onAreasDragMove}
                            onDragEnd={onAreasDragEnd}
                        >
                            <SortableContext
                                items={areas.map((area) => area.area_id.toString())}
                                strategy={verticalListSortingStrategy}
                            >
                                {areas.map((area: IArea) => (
                                    <SortableAreaCard
                                        key={area.area_id}
                                        id={area.area_id.toString()}
                                        isExpanded={areaTabs.includes(area.area_id)}
                                    >
                                        <AreaItem area={area} isDragging={isDraggingAreas} />
                                    </SortableAreaCard>
                                ))}
                            </SortableContext>
                        </DndContext>
                    ) : (
                        areas.map((area: IArea) => <AreaItem key={area.area_id} area={area} />)
                    )}
                </List>
            )}

            <AddAreaOrFunctionalLocationModal
                open={openAreaModal || !!selectedAreaToEdit}
                handleClose={() => handleCloseAreaModal(refetch)}
                title={
                    selectedAreaToEdit
                        ? t("failure_details_setup.edit_area_modal.title")
                        : t("failure_details_setup.add_area_modal.title")
                }
            >
                <AreaCreationForm handleClose={() => handleCloseAreaModal(refetch)} areaToEdit={selectedAreaToEdit} />
            </AddAreaOrFunctionalLocationModal>

            <AddAreaOrFunctionalLocationModal
                open={openFunctionalLocationModal || !!selectedFunctionalLocationToEdit}
                handleClose={() => handleCloseFunctionalLocationModal(refetch)}
                title={
                    selectedFunctionalLocationToEdit
                        ? t("failure_details_setup.edit_functional_location_modal.title")
                        : t("failure_details_setup.add_functional_location_modal.title")
                }
            >
                <FunctionalLocationCreationForm
                    handleClose={() => handleCloseFunctionalLocationModal(refetch)}
                    selectedFunctionalLocationToEdit={selectedFunctionalLocationToEdit}
                />
            </AddAreaOrFunctionalLocationModal>

            <CheckUserPasswordModal
                open={Boolean(selectedAreaToDelete) || Boolean(selectedFunctionalLocationToDelete)}
                onConfirmPassword={Boolean(selectedAreaToDelete) ? handleDeleteArea : handleDeleteFunctionalLocation}
                onClose={handleCloseDeletionModal}
                title={
                    Boolean(selectedAreaToDelete)
                        ? t("failure_details_setup.delete_area_modal.title", { name: selectedAreaToDelete?.name })
                        : t("failure_details_setup.delete_functional_location_modal.title", {
                              name: selectedFunctionalLocationToDelete?.name,
                          })
                }
                question={
                    Boolean(selectedAreaToDelete)
                        ? t("failure_details_setup.delete_area_modal.warning")
                        : t("failure_details_setup.delete_functional_location_modal.warning")
                }
                questionConfirmedButtonText={
                    Boolean(selectedAreaToDelete)
                        ? t("failure_details_setup.delete_area_modal.confirm")
                        : t("failure_details_setup.delete_functional_location_modal.confirm")
                }
            />
        </Box>
    );
};
